import React, { useState, useEffect, useCallback } from "react";
import "./Gallery.scss";
import sort from "../../assets/icons/sort.svg";
import { getApiBaseUrl, apiFetch } from "../../api/client";

const categories = [
  { key: "all", label: "همه عکس‌ها" },
  { key: "exchange", label: "تصاویر صرافی" },
  { key: "coins", label: "سکه‌ها" },
  { key: "currency", label: "ارز" },
  { key: "liscenses", label: "مجوز و گواهینامه" },
];

const imageSrc = (path) =>
  `${getApiBaseUrl()}/${String(path || "").replace(/\\/g, "/")}`;

const Gallery = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [isDropdown, setIsDropdown] = useState(false);
  const [images, setImages] = useState({
    all: [],
    exchange: [],
    coins: [],
    currency: [],
    liscenses: [],
  });
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await apiFetch("/api/images", { method: "GET" });
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const resData = await response.json();
        const data = resData.images || [];

        setImages({
          all: data,
          exchange: data.filter((img) => img.category === "exchange"),
          coins: data.filter((img) => img.category === "coins"),
          currency: data.filter((img) => img.category === "currency"),
          liscenses: data.filter(
            (img) => img.category === "liscenses" || img.category === "liscense"
          ),
        });
      } catch (error) {
        console.error("Error fetching images: ", error);
      }
    };

    fetchImages();
  }, []);

  const list = images[activeCategory] || [];
  const lightboxOpen = lightboxIndex !== null && list[lightboxIndex];

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const showPrev = useCallback(() => {
    setLightboxIndex((i) => (i === null ? null : (i - 1 + list.length) % list.length));
  }, [list.length]);
  const showNext = useCallback(() => {
    setLightboxIndex((i) => (i === null ? null : (i + 1) % list.length));
  }, [list.length]);

  useEffect(() => {
    if (lightboxIndex === null) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") showPrev();
      if (e.key === "ArrowRight") showNext();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [lightboxIndex, closeLightbox, showPrev, showNext]);

  const handleCategoryChange = (key) => {
    setActiveCategory(key);
    setLightboxIndex(null);
    setIsDropdown(false);
  };

  return (
    <div className="gallery-container">
      <div className="title">شرکت تضامنی اشراقی و شرکا</div>

      <div className="content">
        <div className="image-folders desktop-tabs">
          {categories.map((category) => (
            <button
              key={category.key}
              type="button"
              className={activeCategory === category.key ? "active" : ""}
              onClick={() => handleCategoryChange(category.key)}
            >
              {category.label}
            </button>
          ))}
        </div>

        <div className="image-folders mobile-tabs">
          <div className="dropdown">
            <button type="button" onClick={() => setIsDropdown((v) => !v)}>
              <img src={sort} alt="" />
              {categories.find((c) => c.key === activeCategory)?.label ||
                "دسته‌بندی‌ها"}
            </button>
            {isDropdown && (
              <div className="dropdown-menu">
                {categories.map((category) => (
                  <div
                    key={category.key}
                    role="button"
                    tabIndex={0}
                    className={`dropdown-item ${
                      activeCategory === category.key ? "active" : ""
                    }`}
                    onClick={() => handleCategoryChange(category.key)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCategoryChange(category.key);
                    }}
                  >
                    {category.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="images-grid">
          {list.length > 0 ? (
            list.map((image, index) => (
              <button
                type="button"
                key={image.id || index}
                className="gallery-card"
                onClick={() => setLightboxIndex(index)}
              >
                <img
                  src={imageSrc(image.path)}
                  alt={`gallery-${image.id || index}`}
                  loading="lazy"
                />
              </button>
            ))
          ) : (
            <div className="gallery-empty">تصویری یافت نشد</div>
          )}
        </div>
      </div>

      {lightboxOpen && (
        <div
          className="gallery-lightbox"
          role="dialog"
          aria-modal="true"
          onClick={closeLightbox}
        >
          <button
            type="button"
            className="lightbox-close"
            onClick={closeLightbox}
            aria-label="بستن"
          >
            ×
          </button>
          {list.length > 1 && (
            <>
              <button
                type="button"
                className="lightbox-nav prev"
                onClick={(e) => {
                  e.stopPropagation();
                  showPrev();
                }}
                aria-label="قبلی"
              >
                <span className="lightbox-nav-icon" aria-hidden="true">
                  ←
                </span>
                <span className="lightbox-nav-label">قبلی</span>
              </button>
              <button
                type="button"
                className="lightbox-nav next"
                onClick={(e) => {
                  e.stopPropagation();
                  showNext();
                }}
                aria-label="بعدی"
              >
                <span className="lightbox-nav-label">بعدی</span>
                <span className="lightbox-nav-icon" aria-hidden="true">
                  →
                </span>
              </button>
            </>
          )}
          <div
            className="lightbox-content"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={imageSrc(list[lightboxIndex].path)}
              alt={`lightbox-${list[lightboxIndex].id || lightboxIndex}`}
            />
            <div className="lightbox-meta">
              {lightboxIndex + 1} / {list.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
